import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ArticleCard from "@/components/feature/ArticleCard";
import LikeButton from "@/components/feature/LikeButton";
import CommentSection from "@/pages/article/components/CommentSection";
import ShareModal from "@/components/feature/ShareModal";
import { articles, categoryMap } from "@/mocks/articles";
import { useState } from "react";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const [showFullContent, setShowFullContent] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex flex-col items-center justify-center pt-20">
          <span className="w-16 h-16 flex items-center justify-center mb-4">
            <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
          </span>
          <h1 className="text-2xl font-heading font-bold text-foreground-800">文章未找到</h1>
          <Link
            to="/"
            className="mt-6 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const cat = categoryMap[article.category];
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const currentUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <>
      <Navbar />

      {/* SEO Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: article.title,
            image: [article.coverImage],
            datePublished: article.publishedAt,
            author: {
              "@type": "Organization",
              name: "51黑料网",
            },
            publisher: {
              "@type": "Organization",
              name: "51黑料网",
              logo: {
                "@type": "ImageObject",
                url: "/vite.svg",
              },
            },
          }),
        }}
      />

      <main>
        {/* About / Full article reading section */}
        <article className="bg-background-50 pt-24 pb-12 md:pb-16">
          <div className="px-4 md:px-6 max-w-3xl mx-auto">
            {/* Category badge */}
            <div className="flex items-center justify-center mb-6">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={`${cat.icon} text-xs`}></i>
                </span>
                {cat.name}
              </Link>
            </div>

            {/* Title */}
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 text-center leading-tight">
              {article.title}
            </h1>

            {/* Meta */}
            <div className="flex items-center justify-center gap-4 mt-5 text-xs text-foreground-400 flex-wrap">
              <span className="flex items-center gap-1">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-user-line"></i>
                </span>
                {article.author}
              </span>
              <span>{article.publishedAt}</span>
              <span className="flex items-center gap-1">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </span>
                {article.viewCount > 10000
                  ? `${(article.viewCount / 10000).toFixed(1)}万 阅读`
                  : `${article.viewCount} 阅读`}
              </span>
            </div>

            {/* Like button */}
            <div className="flex justify-center mt-4">
              <LikeButton articleId={article.id} initialLikeCount={article.likeCount} />
            </div>

            {/* Cover image */}
            <div className="mt-8 rounded-2xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 51黑料网`}
                className="w-full aspect-video object-cover"
              />
            </div>

            {/* Content */}
            <div className="mt-10">
              <div className="prose prose-base md:prose-lg max-w-none font-body text-foreground-800 leading-relaxed">
                {article.content.split("\n").map((paragraph, idx) => {
                  // Show first 3 paragraphs, rest behind toggle for very long articles
                  if (!showFullContent && idx > 3 && article.content.split("\n").length > 5) {
                    if (idx === 4) {
                      return (
                        <div key={idx} className="text-center py-6">
                          <button
                            onClick={() => setShowFullContent(true)}
                            className="px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            展开阅读全文
                          </button>
                        </div>
                      );
                    }
                    return null;
                  }
                  return (
                    <p key={idx} className="mb-4 text-foreground-700">
                      {paragraph}
                    </p>
                  );
                })}
              </div>
            </div>

            {/* Tags / Share */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-10 pt-6 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                  {cat.name}
                </span>
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                  独家爆料
                </span>
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                  51黑料网
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-foreground-400">分享到：</span>
                <button
                  onClick={() => setShowShareModal(true)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-weibo-line"></i>
                </button>
                <button
                  onClick={() => setShowShareModal(true)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-wechat-line"></i>
                </button>
                <button
                  onClick={() => setShowShareModal(true)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>

            {/* Comment Section */}
            <CommentSection articleId={article.id} />
          </div>
        </article>

        {/* CTA Section - Related articles */}
        <section className="bg-background-100/50 py-16 md:py-20">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            {/* Visual area */}
            <div className="rounded-2xl overflow-hidden mb-12">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20journalism%20and%20media%20collage%20with%20newspaper%20textures%2C%20warm%20golden%20and%20amber%20tones%2C%20editorial%20aesthetic%2C%20artistic%20composition%2C%20soft%20film%20grain%20texture%2C%20clean%20minimal%20design%2C%20sophisticated%20atmosphere&width=1200&height=400&seq=article-cta-2026&orientation=landscape"
                alt="更多爆料推荐"
                title="51黑料网 - 更多精彩爆料"
                className="w-full aspect-[3/1] object-cover"
                loading="lazy"
              />
            </div>

            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                更多<span className="text-primary-600 italic">精彩</span>爆料
              </h2>
              <p className="mt-3 text-foreground-500 text-sm">
                继续浏览，发现更多你感兴趣的内容
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.map((ra) => (
                <ArticleCard key={ra.id} article={ra} />
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-2 px-8 py-3 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部{cat.name}爆料
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={article.title}
        url={currentUrl}
      />
    </>
  );
}